import styled from "styled-components";
import { useState, useEffect } from "react";
import FormStringInput from "../../../../../../components/FormControls/FormStringInput";
import PrimaryButton from "../../../../../../components/FormControls/PrimaryButton";
import SecondaryButton from "../../../../../../components/FormControls/SecondaryButton";
import WarningButton from "../../../../../../components/FormControls/WarningButton";
import request from "../../../../../../api/request";
import MessageBox from "../../../../../../components/MessageBox";
import NarrowGrid from "../../../../../../components/NarrowGrid";
import { NotificationManager } from 'react-notifications';

const StyledBranchFestivalSettings = styled.div`
`;

const WhiteBoard = styled.div`
    background-color: white;
    width: 600px;
    box-sizing: border-box;
    min-height: 543px;
    padding: 30px;
    margin-bottom: 30px;
    color: ${(props) => props.theme.color.gray};
`;

const Title = styled.span`
    display: block;
    color: ${(props) => props.theme.color.primaryblue};
    font-size: 14px;
    font-weight: 600;
    padding-bottom: 5px;
`;

const LogoUploadRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 15px;
    gap: 20px;
`;

const LogoUploadLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LogoFileInputLabel = styled.label`
    display: inline-block;
    cursor: pointer;
    background-color: ${(props) => props.theme.color.primaryblue || '#1a73e8'};
    color: white;
    font-size: 12px;
    padding: 6px 14px;
    border-radius: 3px;
    user-select: none;
    width: fit-content;
    &:hover {
        opacity: 0.85;
    }
`;

const LogoHiddenInput = styled.input`
    display: none;
`;

const LogoFileName = styled.span`
    font-size: 11px;
    color: ${(props) => props.theme.color.gray};
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LogoButtonRow = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 4px;
`;

const LogoPreviewBox = styled.div`
    width: 120px;
    height: 80px;
    border: 1px solid #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9f9f9;
    flex-shrink: 0;
`;

const LogoPreviewImg = styled.img`
    max-width: 118px;
    max-height: 78px;
    object-fit: contain;
`;

const LogoNoImage = styled.span`
    font-size: 11px;
    color: #aaa;
`;

function BranchFestivalSettings(props) {

    const {selectedBranch, isAddBranchMode, setIsAddBranchMode, refreshBranchesCallback} = props;

    const [loadedPage, setLoadedPage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeletingBranch, setIsDeletingBranch] = useState(false);
    const [isDeletingFestival, setIsDeletingFestival] = useState(false);
    const [festivals, setFestivals] = useState([]);
    const [selectedFestival, setSelectedFestival] = useState(null);
    const [selectedFestivalId, setSelectedFestivalId] = useState(null);
    const [isAddFestivalMode, setIsAddFestivalMode] = useState(false);
    const [gridColumns, setGridColumns] = useState([
        {id: 'Name', type: 'String', width: 150, textAlign: 'left'},
        {id: 'Code', type: 'String', width: 70, textAlign: 'center'},
    ]);
    const [gridData, setGridData] = useState({groups: [''], items: [[]]});
    const [restrictedFundName, setRestrictedFundName] = useState('');
    const [restrictedFundCode, setRestrictedFundCode] = useState('');
    const [showMessageBox, setShowMessageBox] = useState(false);
    const [needsValidation, setNeedsValidation] = useState(false);
    const [isAddingDuplicatedFestival, setIsAddingDuplicatedFestival] = useState(false);
    const [festivalLogoImage, setFestivalLogoImage] = useState(null);
    const [festivalLogoPreview, setFestivalLogoPreview] = useState(null);
    const [savedFestivalLogoPreview, setSavedFestivalLogoPreview] = useState(null);

    // Build the composite key the backend uses to identify a festival row.
    const festivalKeyFor = (festivalRow) => ({
        RecordType: selectedBranch.RecordType,
        BranchCommitteeCode: selectedBranch.BranchCommitteeCode,
        DivisionCode: selectedBranch.DivisionCode,
        RestrictedFundCode: festivalRow.Code,
    });

    // Find the full festival record (which carries LogoImage) for a grid item.
    const findFullFestival = (gridItem) => {
        if (!gridItem || !selectedBranch) return null;
        return festivals.find((f) =>
            f.RecordType === selectedBranch.RecordType &&
            f.BranchCommitteeCode === selectedBranch.BranchCommitteeCode &&
            f.DivisionCode === selectedBranch.DivisionCode &&
            f.RestrictedFundCode.replace(/\s/g, '') === gridItem.Code.replace(/\s/g, '')
        ) || null;
    };

    const loadFestivalLogo = (gridItem) => {
        const full = findFullFestival(gridItem);
        setFestivalLogoImage(null);
        const preview = full && full.LogoImage
            ? `data:image/png;base64,${full.LogoImage}`
            : null;
        setFestivalLogoPreview(preview);
        setSavedFestivalLogoPreview(preview);
    };

    const onSelectGridItem = (item) => {
        setSelectedFestivalId(item.Id);
        setSelectedFestival(item);
        setRestrictedFundCode(item.Code);
        setRestrictedFundName(item.Name);
        loadFestivalLogo(item);
    }
    
    const fetchFestivals = async (branchCommitteeCodeToSelect, restrictedFundCodeToSelect) => {
        try {
            let festivalList = [];

            const resp = (await request('database', 'fetchFestivals'));
            if (resp.ok) {
                resp.data.map((festival, index) => {
                    festivalList.push(festival);
                });
                setFestivals(festivalList);
                rebuildGridData(festivalList, branchCommitteeCodeToSelect, restrictedFundCodeToSelect);
            } else {
                console.log('Failed fetchFestivals');
                NotificationManager.error(resp.data);
                return false;
            }
        } catch (e) {
            console.log('Failed to fetch festivals: ', e);
            return false;
        }
        return true;
    }

    const onDeleteFestival = async () => {
        if (selectedFestival) {
            setIsDeletingFestival(true);
            let deleteData = {};
            deleteData.RecordType = selectedBranch.RecordType;
            deleteData.BranchCommitteeCode = selectedBranch.BranchCommitteeCode;
            deleteData.DivisionCode = selectedBranch.DivisionCode;
            deleteData.RestrictedFundCode = restrictedFundCode;
            deleteData.RestrictedFundName = restrictedFundName;

            const resp = (await request('database', 'deleteBranchFestival', [deleteData]));
            if (resp.ok) {
                setSelectedFestival(null);
                setSelectedFestivalId(null);
                fetchFestivals(selectedBranch.BranchCommitteeCode, selectedFestival ? selectedFestival.RestrictedFundCode : null);
                NotificationManager.success('Successfully deleted branch festival');
            } else
            {
                console.log(resp.data);
                NotificationManager.error(resp.data);
            }
            setIsDeletingFestival(false);
        }
    }

    const onSaveChanges = async () => {
        if (isAddFestivalMode) { // adding new item
            if (restrictedFundCode === '' || restrictedFundName === '') {
                setNeedsValidation(true);
            } else {
                // check duplicated code
                if (gridData.items[0].length > 0 && (gridData.items[0].find((itm) => itm.Code.replace(/\s/g, '') === restrictedFundCode.replace(/\s/g, '') || itm.Name.replace(/\s/g, '') === restrictedFundName.replace(/\s/g, '')))) {
                    setIsAddingDuplicatedFestival(true);
                } else {
                    setIsSaving(true);
                    let saveData = {};
                    saveData.RecordType = selectedBranch.RecordType;
                    saveData.BranchCommitteeCode = selectedBranch.BranchCommitteeCode;
                    saveData.DivisionCode = selectedBranch.DivisionCode;
                    saveData.RestrictedFundCode = restrictedFundCode;
                    saveData.RestrictedFundName = restrictedFundName;

                    const resp = (await request('database', 'addBranchFestival', [saveData]));
                    if (resp.ok) {
                        // If a logo was picked while creating, upload it to the new row.
                        if (festivalLogoImage) {
                            const logoResp = await request('database', 'uploadFestivalLogo', [{
                                RecordType: selectedBranch.RecordType,
                                BranchCommitteeCode: selectedBranch.BranchCommitteeCode,
                                DivisionCode: selectedBranch.DivisionCode,
                                RestrictedFundCode: restrictedFundCode,
                            }, festivalLogoImage]);
                            if (logoResp.ok) {
                                setSavedFestivalLogoPreview(festivalLogoPreview);
                                setFestivalLogoImage(null);
                            } else {
                                NotificationManager.error(logoResp.data || 'Logo upload failed');
                            }
                        }
                        fetchFestivals(selectedBranch.BranchCommitteeCode, restrictedFundCode);
                        NotificationManager.success('Successfully added branch festival');
                    } else
                    {
                        console.log(resp.data);
                        NotificationManager.error(resp.data);
                    }
                    setIsSaving(false);
                    setIsAddFestivalMode(false);
                }
            }
        } else {
            if (selectedFestival && restrictedFundName === '') {
                setNeedsValidation(true);
            } else {
                // check duplicated code
                if (gridData.items[0].length > 0 && selectedFestival && gridData.items[0].find((itm) => itm.Id !== selectedFestival.Id && itm.Name.replace(/\s/g, '') === restrictedFundName.replace(/\s/g, ''))) {
                    setIsAddingDuplicatedFestival(true);
                } else {
                    setIsSaving(true);
                    let saveData = {};
                    saveData.RecordType = selectedBranch.RecordType;
                    saveData.BranchCommitteeCode = selectedBranch.BranchCommitteeCode;
                    saveData.DivisionCode = selectedBranch.DivisionCode;
                    saveData.RestrictedFundCode = selectedFestival.Code;
                    saveData.OldRestrictedFundName = selectedFestival.Name;
                    saveData.NewRestrictedFundName = restrictedFundName;

                    const resp = (await request('database', 'updateBranchFestival', [saveData]));
                    if (resp.ok) {
                        // If a new logo file was selected, upload it as part of saving.
                        if (festivalLogoImage) {
                            const logoResp = await request('database', 'uploadFestivalLogo', [festivalKeyFor(selectedFestival), festivalLogoImage]);
                            if (logoResp.ok) {
                                setSavedFestivalLogoPreview(festivalLogoPreview);
                                setFestivalLogoImage(null);
                            } else {
                                NotificationManager.error(logoResp.data || 'Logo upload failed');
                            }
                        }
                        fetchFestivals(selectedBranch.BranchCommitteeCode, selectedFestival ? selectedFestival.Code : null);
                        NotificationManager.success('Successfully updated branch festival');
                    } else
                    {
                        console.log(resp.data);
                        NotificationManager.error(resp.data);
                    }
                    setIsSaving(false);
                }
            }
        }
    }

    const deleteBranch = async () => {
        setShowMessageBox(false);

        setIsDeletingBranch(true);
        let deleteData = {};
        deleteData.RecordType = selectedBranch.RecordType;
        deleteData.RecordName = selectedBranch.RecordName;
        deleteData.BranchCommitteeCode = selectedBranch.BranchCommitteeCode;
        const resp = (await request('database', 'deleteBranch', [deleteData]));
        if (resp.ok && refreshBranchesCallback) {
            refreshBranchesCallback();
            NotificationManager.success('Branch deleted');
        } else
        {
            console.log(resp.data);
            NotificationManager.error(resp.data);
        }
        setIsDeletingBranch(false);
    }

    const onChangeFestivalName = (txt) => {
        setRestrictedFundName(txt);
    }

    const onFestivalLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFestivalLogoImage(file);
        setFestivalLogoPreview(URL.createObjectURL(file));
    };

    const onRevertFestivalLogo = () => {
        setFestivalLogoImage(null);
        setFestivalLogoPreview(savedFestivalLogoPreview);
    };

    const onDeleteFestivalLogo = async () => {
        if (!selectedFestival) return;
        const resp = await request('database', 'removeFestivalLogo', [festivalKeyFor(selectedFestival)]);
        if (resp.ok) {
            setFestivalLogoImage(null);
            setFestivalLogoPreview(null);
            setSavedFestivalLogoPreview(null);
            fetchFestivals(selectedBranch.BranchCommitteeCode, selectedFestival.Code);
            NotificationManager.success('Logo removed');
        } else {
            NotificationManager.error(resp.data || 'Failed to remove logo');
        }
    };

    const rebuildGridData = (festivals, branchCommitteeCodeToSelect, restrictedFundCodeToSelect) => {
        if (festivals && selectedBranch && festivals.length > 0) {
            let festivalList = [];
            festivals.map((festival, index) => {
                if (festival.RecordType === selectedBranch.RecordType && festival.BranchCommitteeCode === selectedBranch.BranchCommitteeCode && festival.DivisionCode === selectedBranch.DivisionCode) {
                    let gridItem = {Id: index, Name: festival.RestrictedFundName, Code: festival.RestrictedFundCode, Ident: festival.Ident};
                    festivalList.push(gridItem);
                    if (branchCommitteeCodeToSelect && restrictedFundCodeToSelect && festival.BranchCommitteeCode === branchCommitteeCodeToSelect && festival.RestrictedFundCode.replace(/\s/g, '') === restrictedFundCodeToSelect.replace(/\s/g, '')) {
                        setSelectedFestivalId(index);
                        setSelectedFestival(gridItem);
                    }
                }
            });
            setGridData({groups: [''], items: [festivalList]});
        }
    }

    useEffect(() => {
        if (!loadedPage) {
            fetchFestivals();
            setLoadedPage(true);
        }
        rebuildGridData(festivals);
        setSelectedFestival(null);
        setSelectedFestivalId(null);
        setRestrictedFundCode('');
        setRestrictedFundName('');
        setNeedsValidation(false);
        setFestivalLogoImage(null);
        setFestivalLogoPreview(null);
        setSavedFestivalLogoPreview(null);
    }, [selectedBranch]);

    useEffect(() => {
        if (isAddFestivalMode) {
            setNeedsValidation(false);
            setRestrictedFundCode('');
            setRestrictedFundName('');
            setFestivalLogoImage(null);
            setFestivalLogoPreview(null);
            setSavedFestivalLogoPreview(null);
        } else if (selectedFestival) {
            setNeedsValidation(false);
            setRestrictedFundCode(selectedFestival.Code ? selectedFestival.Code : '');
            setRestrictedFundName(selectedFestival.Name ? selectedFestival.Name : '');
            loadFestivalLogo(selectedFestival);
        }
    }, [isAddFestivalMode]);

    useEffect(() => {
        if (selectedFestival) {
            setRestrictedFundCode(selectedFestival.Code ? selectedFestival.Code : '');
            setRestrictedFundName(selectedFestival.Name ? selectedFestival.Name : '');
            loadFestivalLogo(selectedFestival);
        } else {
            setRestrictedFundCode('');
            setRestrictedFundName('');
            setFestivalLogoImage(null);
            setFestivalLogoPreview(null);
            setSavedFestivalLogoPreview(null);
        }
    }, [selectedFestival]);

    useEffect(() => {
        setIsAddingDuplicatedFestival(false);
        setNeedsValidation(false);
    }, [selectedBranch, selectedFestival, isAddFestivalMode, restrictedFundCode, restrictedFundName]);

    return (
        <StyledBranchFestivalSettings>
            <WhiteBoard>
                <Title>Festivals</Title>
                <div style={{display:'flex'}}>
                    <div>
                        <NarrowGrid id='branchsetting_festivals_grid' gridColumns={gridColumns} gridData={gridData} disabled={isAddFestivalMode} itemIdToSelect={selectedFestivalId} onSelectGridItem={onSelectGridItem} gridStyle={{width:220, height:350, marginTop:20}}></NarrowGrid>
                        <div style={{marginTop:20}}>
                            {selectedFestivalId !== null && !isAddFestivalMode && <SecondaryButton caption='Delete' isLoading={isDeletingFestival} onClick={() => onDeleteFestival()} style={{width:75, height:25}}></SecondaryButton>}
                            {isAddFestivalMode && <SecondaryButton caption='Cancel' onClick={() => setIsAddFestivalMode(false)} style={{width:75, height:25, float:'right'}}></SecondaryButton>}
                            {!isAddFestivalMode && <PrimaryButton caption='Create new' onClick={() => setIsAddFestivalMode(true)} style={{width:100, height:25, float:'right'}}></PrimaryButton>}
                        </div>
                    </div>
                    <div style={{marginLeft:30}}>
                    <FormStringInput label='Code' value={restrictedFundCode} onChange={(val) => setRestrictedFundCode(val)} inputStyle={{width:100, height:35}} validated={needsValidation && restrictedFundCode === '' ? false : undefined} disabled={!isAddFestivalMode}></FormStringInput>
                    <FormStringInput label='Name' value={restrictedFundName} onChange={(val) => onChangeFestivalName(val)} inputStyle={{width:280, height:35}} validated={needsValidation && restrictedFundName === '' ? false : undefined} disabled={selectedFestivalId === null && !isAddFestivalMode} ></FormStringInput>
                    {isAddingDuplicatedFestival && <div style={{marginTop:5, width:280, fontSize:12, fontWeight:700, color:'#dc3545'}}>* Code or name is duplicated.</div>}

                    {(selectedFestival || isAddFestivalMode) && <>
                        <Title style={{paddingTop:20}}>Custom festival logo</Title>
                        <LogoUploadRow>
                            <LogoUploadLeft>
                                <LogoFileInputLabel>
                                    Choose image
                                    <LogoHiddenInput
                                        type="file"
                                        accept="image/png, image/jpeg, image/gif"
                                        onChange={onFestivalLogoFileChange}
                                    />
                                </LogoFileInputLabel>
                                {festivalLogoImage && (
                                    <LogoFileName>{festivalLogoImage.name}</LogoFileName>
                                )}
                                {festivalLogoImage && savedFestivalLogoPreview && (
                                    <LogoButtonRow>
                                        <SecondaryButton
                                            caption='Revert'
                                            onClick={onRevertFestivalLogo}
                                            style={{width:80}}
                                        />
                                    </LogoButtonRow>
                                )}
                                {!festivalLogoImage && savedFestivalLogoPreview && !isAddFestivalMode && (
                                    <LogoButtonRow>
                                        <WarningButton
                                            caption='Delete logo'
                                            onClick={onDeleteFestivalLogo}
                                            style={{width:100}}
                                        />
                                    </LogoButtonRow>
                                )}
                            </LogoUploadLeft>
                            <LogoPreviewBox>
                                {festivalLogoPreview
                                    ? <LogoPreviewImg src={festivalLogoPreview} alt="Festival logo preview" />
                                    : <LogoNoImage>No logo</LogoNoImage>
                                }
                            </LogoPreviewBox>
                        </LogoUploadRow>
                    </>}
                    </div>
                </div>
            </WhiteBoard>
            <div>
                {!isAddBranchMode && <WarningButton caption='Delete branch' isLoading={isDeletingBranch} onClick={() => setShowMessageBox(true)} style={{width:120}}></WarningButton>}
                {isAddBranchMode && <SecondaryButton caption='Cancel' onClick={() => setShowMessageBox(true)} style={{width:100}}></SecondaryButton>}
                <PrimaryButton caption={isAddFestivalMode ? 'Add festival' : 'Save changes'} isLoading={isSaving} onClick={() => onSaveChanges()} disabled={isAddFestivalMode || selectedFestival ? false : true} style={{width:150, float:'right'}}></PrimaryButton>
            </div>

            { showMessageBox && <MessageBox
                text = {isAddBranchMode ? 'Are you sure cancel?' : 'Are you sure you want to delete this branch?'}
                yesText = {isAddBranchMode ? 'Cancel' : 'Delete branch'}
                noText = {isAddBranchMode ? 'No' : 'Cancel'}
                isWarning = {!isAddBranchMode}
                handleClickYes = {() => {
                        if (isAddBranchMode) {
                            if (setIsAddBranchMode) {
                                setIsAddBranchMode(false);
                                setShowMessageBox(false);
                            }
                        } else {
                            deleteBranch();
                        }
                    }
                }
                handleClickNo = {() => setShowMessageBox(false)}
            />}
        </StyledBranchFestivalSettings>
    )
}

export default BranchFestivalSettings;